import { useApi, useLoad } from "@allergy123/api-client/react";
import { ALLERGEN_CATEGORIES, ANIMAL_CLASSES, CONTROL_KINDS, EXPOSURES, type AntigenDetail, type UpdateAntigenRequest } from "@allergy123/contracts/allergens";
import { Button, Card, Field, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { CatalogHeader, errorText } from "./common";

const options = (values: readonly string[], none?: string) => [...(none ? [{ value: "", label: none }] : []), ...values.map((v) => ({ value: v, label: v }))];

/** Keyed by id, so the editor starts fresh for each antigen and its form is never carried over from another one. */
export function AntigenPage() {
  const { id = "" } = useParams();
  return <AntigenEditor key={id} id={id} />;
}

function AntigenEditor({ id }: { id: string }) {
  const api = useApi();
  const data = useLoad((a) => a.admin.catalog.antigen(id));
  const items = useLoad((a) => a.admin.catalog.items());
  const [form, setForm] = useState<AntigenDetail | null>(null);
  const [saved, setSaved] = useState<AntigenDetail | null>(null);
  const [link, setLink] = useState("");
  const [note, setNote] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // The form takes the antigen once, when it first loads, and `saved` is what the server last returned. Nothing reloads over
  // what is being edited: Save sets both from the saved record, and Link and Unlink replace only the products.
  useEffect(() => {
    const loaded = data.data;
    if (loaded) {
      setForm((f) => f ?? loaded);
      setSaved((s) => s ?? loaded);
    }
  }, [data.data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !form || !saved) return;
    const before = saved;
    const patch: UpdateAntigenRequest = {};
    for (const key of ["displayName", "abbreviation", "genusSpecies", "category", "controlKind", "crossReactionGroup", "exposure", "animalClass", "diagnosisCode", "highProtease", "isOffered"] as const) {
      if (form[key] !== before[key]) (patch as Record<string, unknown>)[key] = form[key];
    }
    if (Object.keys(patch).length === 0) {
      setNote({ kind: "info", text: "Nothing to save." });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const updated = await api.admin.catalog.updateAntigen(id, patch);
      setSaved(updated);
      setForm(updated);
      setNote({ kind: "info", text: "Saved." });
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const replaceProducts = (record: AntigenDetail) => setForm((f) => (f ? { ...f, products: record.products, productCount: record.productCount } : f));

  async function linkProduct() {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      replaceProducts(await api.admin.catalog.linkProduct(id, link));
      setLink("");
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function unlink(itemId: string) {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      replaceProducts(await api.admin.catalog.unlinkProduct(id, itemId));
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const set = <K extends keyof AntigenDetail>(key: K, value: AntigenDetail[K]) => setForm((f) => (f ? { ...f, [key]: value } : f));
  return (
    <>
      <CatalogHeader title={saved?.displayName ?? "Antigen"} intro="Names, classification and linked products. The legacy number is kept for traceability." />
      {data.state === "error" && <Status kind="error" label={`Couldn't load the antigen (${errorText(data.error)})`} />}
      {note && <div className="mb-6"><Status kind={note.kind} label={note.text} /></div>}
      {form && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card as="section">
            <form onSubmit={(e) => void save(e)} className="grid gap-4">
              <Field label="Display name" value={form.displayName} onChange={(e) => set("displayName", e.target.value)} required />
              <Field label="Abbreviation" value={form.abbreviation} onChange={(e) => set("abbreviation", e.target.value.toUpperCase())} maxLength={10} required hint="At most 10 characters; unique across antigens." />
              <Field label="Genus and species" value={form.genusSpecies ?? ""} onChange={(e) => set("genusSpecies", e.target.value || null)} />
              <Select label="Category" value={form.category} onChange={(e) => set("category", e.target.value as AntigenDetail["category"])} options={options(ALLERGEN_CATEGORIES)} />
              <Select label="Control kind" value={form.controlKind ?? ""} onChange={(e) => set("controlKind", (e.target.value || null) as AntigenDetail["controlKind"])} options={options(CONTROL_KINDS, "Not a control")} />
              <Select label="Exposure" value={form.exposure ?? ""} onChange={(e) => set("exposure", (e.target.value || null) as AntigenDetail["exposure"])} options={options(EXPOSURES, "Not set")} />
              <Select label="Animal class" value={form.animalClass ?? ""} onChange={(e) => set("animalClass", (e.target.value || null) as AntigenDetail["animalClass"])} options={options(ANIMAL_CLASSES, "Not an animal")} />
              <Field label="Diagnosis code (ICD-10)" value={form.diagnosisCode ?? ""} onChange={(e) => set("diagnosisCode", e.target.value || null)} />
              <Field label="Cross-reactivity group" type="number" value={form.crossReactionGroup ?? ""} onChange={(e) => set("crossReactionGroup", e.target.value === "" ? null : Number(e.target.value))} />
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.highProtease} onChange={(e) => set("highProtease", e.target.checked)} /> High protease</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.isOffered} onChange={(e) => set("isOffered", e.target.checked)} /> Offered</label>
              <p className="text-small text-ink-secondary">Legacy antigen id: {form.legacyAntigenId ?? "none"} · legacy name: {form.a123Name ?? "none"}</p>
              <div>
                <Button type="submit" disabled={busy}>Save changes</Button>
              </div>
            </form>
          </Card>
          <Card as="section">
            <h2 className="mb-4 text-h3 font-semibold text-anchor">Products</h2>
            <Table caption={`${form.products.length} linked`}>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Procurement</Th>
                  <Th>NDC</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {form.products.map((p) => (
                  <tr key={p.id}>
                    <Td>{p.manufacturerProductName}</Td>
                    <Td>{p.procurementStatus}</Td>
                    <Td>{p.ndc11 ?? <span className="text-ink-secondary">none</span>}</Td>
                    <Td>
                      <Button variant="secondary" surface="card" disabled={busy} onClick={() => void unlink(p.id)}>Unlink</Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="mt-4 grid gap-2">
              {items.state === "error" && <Status kind="error" label={`Couldn't load products (${errorText(items.error)})`} />}
              <Select label="Link a product" value={link} onChange={(e) => setLink(e.target.value)} options={[{ value: "", label: "Choose a product" }, ...(items.data?.items ?? []).map((i) => ({ value: i.id, label: i.manufacturerProductName }))]} />
              <div>
                <Button variant="secondary" disabled={!link || busy} onClick={() => void linkProduct()}>Link product</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
